import React, { useState, useEffect } from 'react';
import {
  Stack,
  Chip,
  Box,
  Alert,
  IconButton,
  CircularProgress,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import modelsService from '../../services/modelsService';
import BaseDialog from './BaseDialog';
import BaseTextField from './BaseTextField';
import BaseButton from './BaseButton';
import PromptChatDialog from './PromptChatDialog';

/**
 * Dialog pour éditer un modèle de prompt existant
 * @param {boolean} open - État d'ouverture du dialog
 * @param {function} onClose - Callback de fermeture
 * @param {function} onSuccess - Callback de succès (optionnel)
 * @param {string} modelId - ID du modèle à éditer
 * @param {boolean} isAdmin - Si l'utilisateur est administrateur
 */
const EditModelDialog = ({ open, onClose, onSuccess, modelId, isAdmin = false }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [prompt, setPrompt] = useState('');
  const [participants, setParticipants] = useState([]);
  const [newParticipant, setNewParticipant] = useState({ displayName: '', title: '' });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [originalModel, setOriginalModel] = useState(null);
  const [showPromptChat, setShowPromptChat] = useState(false);
  const { currentUser } = useAuth();
  const { addSystemNotification } = useNotifications();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Charger les données du modèle
  useEffect(() => {
    if (open && modelId) {
      loadModel();
    }
  }, [open, modelId]);

  const loadModel = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const model = await modelsService.getModel(modelId);
      if (model) {
        // Vérifier les permissions - les admins peuvent modifier tous les modèles
        if (!isAdmin && model.createdBy !== currentUser?.uid) {
          setError('Vous n\'avez pas les droits pour modifier ce modèle');
          return;
        }

        setOriginalModel(model);
        setTitle(model.title || '');
        setDescription(model.description || '');
        setTags(model.tags || []);
        setPrompt(model.prompt || '');
        setParticipants(model.participants || []);
      } else {
        setError('Modèle non trouvé');
      }
    } catch (error) {
      addSystemNotification('Erreur lors du chargement du modèle:', 'error');
      setError('Erreur lors du chargement du modèle');
    } finally {
      setLoading(false);
    }
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setTags([]);
    setTagInput('');
    setPrompt('');
    setParticipants([]);
    setNewParticipant({ displayName: '', title: '' });
    setError(null);
    setOriginalModel(null);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleAddParticipant = () => {
    if (!newParticipant.displayName.trim()) return;
    setParticipants([...participants, { ...newParticipant }]);
    setNewParticipant({ displayName: '', title: '' });
  };

  const handleRemoveParticipant = (index) => {
    const updatedParticipants = [...participants];
    updatedParticipants.splice(index, 1);
    setParticipants(updatedParticipants);
  };

  const handlePromptGenerated = (generatedPrompt) => {
    setPrompt(generatedPrompt);
    setShowPromptChat(false);
  };

  const openPromptChat = () => {
    setShowPromptChat(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title.trim() || !prompt.trim()) {
      setError('Le titre et le prompt sont requis');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await modelsService.updateModel(modelId, {
        title: title.trim(),
        description: description.trim(),
        tags,
        prompt: prompt.trim(),
        participants
      });

      // Callback de succès si fourni
      if (onSuccess) {
        onSuccess(modelId);
      }
      
      // Fermer le dialog et réinitialiser
      resetForm();
      onClose();
      
    } catch (error) {
      addSystemNotification('Erreur lors de la modification du modèle:', 'error');
      setError('Erreur lors de la modification du modèle');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEditingAsAdmin = isAdmin && originalModel && originalModel.createdBy !== currentUser?.uid;

  // Actions du dialog
  const dialogActions = (
    <>
      <BaseButton
        variant="outlined"
        onClick={handleClose}
        disabled={isSubmitting}
      >
        Annuler
      </BaseButton>
      <BaseButton
        gradient
        onClick={handleSubmit}
        disabled={isSubmitting || !title.trim() || !prompt.trim()}
        sx={{ minWidth: '140px' }}
      >
        {isSubmitting ? (
          <>
            <CircularProgress size={18} sx={{ mr: 1, color: 'white' }} />
            Modification...
          </>
        ) : (
          <>
            <EditIcon sx={{ mr: 1, fontSize: '16px' }} />
            Enregistrer
          </>
        )}
      </BaseButton>
    </>
  );

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title="✏️ Modifier le Modèle"
      subtitle="Modifiez votre template de résumé"
      maxWidth="md"
      actions={dialogActions}
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#3F51B5' }} />
        </Box>
      ) : error && !title ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : (
        <form onSubmit={handleSubmit}>
          {isEditingAsAdmin && (
            <Alert severity="info" sx={{ mb: 3, borderRadius: '12px' }}>
              Vous modifiez un modèle créé par un autre utilisateur en tant qu'administrateur.
            </Alert>
          )}
          
          <Grid container spacing={4}>
            {/* Titre du modèle */}
            <Grid item xs={12}>
              <Box sx={{ 
                background: 'white',
                borderRadius: '16px',
                p: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #e3f2fd'
              }}>
                <BaseTextField
                  label="Titre du modèle"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  required
                  fullWidth
                  autoFocus
                  placeholder="Ex: Résumé détaillé de réunion"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: '#3F51B5', fontSize: '20px' }}>📝</Box>
                    )
                  }}
                />
              </Box>
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <Box sx={{ 
                background: 'white',
                borderRadius: '16px',
                p: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #e3f2fd'
              }}>
                <BaseTextField
                  label="Description (optionnelle)"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  placeholder="Description optionnelle du modèle"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: '#3F51B5', fontSize: '20px', alignSelf: 'flex-start', mt: 1 }}>📄</Box>
                    )
                  }}
                />
              </Box>
            </Grid>
            
            {/* Tags */}
            <Grid item xs={12}>
              <Box sx={{ 
                background: 'white',
                borderRadius: '16px',
                p: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #e3f2fd'
              }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    mb: 3,
                    fontWeight: 700,
                    color: '#3F51B5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  🏷️ Tags
                </Typography>
                
                <BaseTextField
                  label="Ajouter un tag"
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  fullWidth
                  placeholder="Ex: réunion, détaillé, format"
                  helperText="Appuyez sur Entrée pour ajouter"
                />
                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                  {tags.map((tag, idx) => (
                    <Chip 
                      key={idx} 
                      label={tag} 
                      onDelete={() => handleDeleteTag(tag)} 
                      sx={{
                        background: 'linear-gradient(135deg, #3F51B5 0%, #5C6BC0 100%)',
                        color: 'white',
                        '& .MuiChip-deleteIcon': {
                          color: 'rgba(255,255,255,0.8)',
                          '&:hover': {
                            color: 'white'
                          }
                        }
                      }}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            </Grid>
            
            {/* Participants */}
            <Grid item xs={12}>
              <Box sx={{
                background: 'white',
                borderRadius: '16px',
                p: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #e3f2fd'
              }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    color: '#3F51B5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  👥 Participants (optionnel)
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                  Définissez les participants types de vos réunions. Ces informations seront automatiquement 
                  remplies lors de la création d'une nouvelle réunion avec ce modèle.
                </Typography>

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <BaseTextField
                      label="Nom du participant"
                      value={newParticipant.displayName}
                      onChange={(e) => setNewParticipant({ ...newParticipant, displayName: e.target.value })}
                      fullWidth
                      placeholder="Ex: John Doe"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddParticipant();
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <BaseTextField
                      label="Fonction (optionnel)"
                      value={newParticipant.title}
                      onChange={(e) => setNewParticipant({ ...newParticipant, title: e.target.value })}
                      fullWidth
                      placeholder="Ex: Directeur"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddParticipant();
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <BaseButton
                      onClick={handleAddParticipant}
                      disabled={!newParticipant.displayName.trim()}
                      startIcon={<AddIcon />}
                      sx={{ height: '56px', width: '100%' }}
                    >
                      Ajouter
                    </BaseButton>
                  </Grid>
                </Grid>

                {participants.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#666' }}>
                      Participants définis ({participants.length})
                    </Typography>
                    <Stack spacing={1}>
                      {participants.map((participant, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: '8px',
                            backgroundColor: '#f8f9fa'
                          }}
                        >
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {participant.displayName}
                            </Typography>
                            {participant.title && (
                              <Typography variant="body2" color="text.secondary">
                                {participant.title}
                              </Typography>
                            )}
                          </Box>
                          <IconButton
                            onClick={() => handleRemoveParticipant(index)}
                            color="error"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)'
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </Grid>
            
            {/* Prompt système */}
            <Grid item xs={12}>
              <Box sx={{ 
                background: 'white',
                borderRadius: '16px',
                p: 3,
                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                border: '1px solid #e3f2fd'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#3F51B5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flex: 1
                    }}
                  >
                    🤖 Prompt système
                  </Typography>
                  <Tooltip title="💬 Besoin d'aide ? Cliquez pour que l'IA vous guide !">
                    <Box
                      sx={{
                        position: 'relative',
                        display: 'inline-block',
                        
                        // Bulle de BD principale avec effet typing
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: '-50px',
                          right: '-15px',
                          background: 'white',
                          border: '2px solid #FF6B6B',
                          borderRadius: '20px 20px 20px 4px',
                          padding: '8px 14px',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          color: '#FF6B6B',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                          animation: 'typingBubble 8s ease-in-out infinite',
                          transformOrigin: 'bottom left',
                          zIndex: 10,
                          whiteSpace: 'nowrap',
                          minWidth: '60px',
                          textAlign: 'center'
                        },
                        
                        '@keyframes typingBubble': {
                          '0%': {
                            opacity: 0,
                            transform: 'scale(0.3) rotate(-8deg)',
                            content: '""'
                          },
                          '8%': {
                            opacity: 1,
                            transform: 'scale(1) rotate(0deg)',
                            content: '"💭"'
                          },
                          '12%': {
                            content: '"B"'
                          },
                          '14%': {
                            content: '"Be"'
                          },
                          '16%': {
                            content: '"Bes"'
                          },
                          '18%': {
                            content: '"Beso"'
                          },
                          '20%': {
                            content: '"Besoi"'
                          },
                          '22%': {
                            content: '"Besoin"'
                          },
                          '25%': {
                            content: '"Besoin d"'
                          },
                          '27%': {
                            content: '"Besoin d\'"'
                          },
                          '29%': {
                            content: '"Besoin d\'a"'
                          },
                          '31%': {
                            content: '"Besoin d\'ai"'
                          },
                          '33%': {
                            content: '"Besoin d\'aid"'
                          },
                          '35%': {
                            content: '"Besoin d\'aide"'
                          },
                          '38%': {
                            content: '"Besoin d\'aide p"'
                          },
                          '40%': {
                            content: '"Besoin d\'aide po"'
                          },
                          '42%': {
                            content: '"Besoin d\'aide pou"'
                          },
                          '44%': {
                            content: '"Besoin d\'aide pour"'
                          },
                          '47%': {
                            content: '"Besoin d\'aide pour g"'
                          },
                          '49%': {
                            content: '"Besoin d\'aide pour gé"'
                          },
                          '51%': {
                            content: '"Besoin d\'aide pour gén"'
                          },
                          '53%': {
                            content: '"Besoin d\'aide pour géné"'
                          },
                          '55%': {
                            content: '"Besoin d\'aide pour génér"'
                          },
                          '57%': {
                            content: '"Besoin d\'aide pour génère"'
                          },
                          '59%': {
                            content: '"Besoin d\'aide pour générer"'
                          },
                          '62%': {
                            content: '"Besoin d\'aide pour générer u"'
                          },
                          '64%': {
                            content: '"Besoin d\'aide pour générer un"'
                          },
                          '55%': {
                            content: '"Besoin d\'aide pour générer un p"'
                          },
                          '56%': {
                            content: '"Besoin d\'aide pour générer un pr"'
                          },
                          '57%': {
                            content: '"Besoin d\'aide pour générer un pro"'
                          },
                          '58%': {
                            content: '"Besoin d\'aide pour générer un prom"'
                          },
                          '59%': {
                            content: '"Besoin d\'aide pour générer un promp"'
                          },
                          '60%': {
                            content: '"Besoin d\'aide pour générer un prompt"'
                          },
                          '61%': {
                            content: '"Besoin d\'aide pour générer un prompt p"'
                          },
                          '62%': {
                            content: '"Besoin d\'aide pour générer un prompt pa"'
                          },
                          '63%': {
                            content: '"Besoin d\'aide pour générer un prompt par"'
                          },
                          '64%': {
                            content: '"Besoin d\'aide pour générer un prompt parf"'
                          },
                          '65%': {
                            content: '"Besoin d\'aide pour générer un prompt parfa"'
                          },
                          '66%': {
                            content: '"Besoin d\'aide pour générer un prompt parfai"'
                          },
                          '67%': {
                            content: '"Besoin d\'aide pour générer un prompt parfait"'
                          },
                          '68%': {
                            content: '"Besoin d\'aide pour générer un prompt parfait ?"'
                          },
                          '70%': {
                            content: '"Besoin d\'aide pour générer un prompt parfait ? 🤖"'
                          },
                          '90%': {
                            content: '"Besoin d\'aide pour générer un prompt parfait ? 🤖"',
                            opacity: 1,
                            transform: 'scale(1) rotate(0deg)'
                          },
                          '95%': {
                            opacity: 1,
                            transform: 'scale(1) rotate(0deg)'
                          },
                          '100%': {
                            opacity: 0,
                            transform: 'scale(0.3) rotate(8deg)',
                            content: '"💭"'
                          }
                        },
                        
                        '@keyframes iconBounce': {
                          '0%, 100%': {
                            transform: 'translateY(0px) scale(1)'
                          },
                          '50%': {
                            transform: 'translateY(-3px) scale(1.05)'
                          }
                        },
                        
                        '@keyframes iconGlow': {
                          '0%': {
                            boxShadow: '0 0 20px rgba(255, 107, 107, 0.5)'
                          },
                          '50%': {
                            boxShadow: '0 0 30px rgba(255, 107, 107, 0.8), 0 0 40px rgba(255, 140, 140, 0.4)'
                          },
                          '100%': {
                            boxShadow: '0 0 20px rgba(255, 107, 107, 0.5)'
                          }
                        }
                      }}
                    >
                      <IconButton
                        onClick={openPromptChat}
                        sx={{
                          position: 'relative',
                          zIndex: 1,
                          background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
                          color: 'white',
                          animation: 'iconBounce 2.5s ease-in-out infinite, iconGlow 4s ease-in-out infinite',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #FF5555 0%, #FF7777 100%)',
                            transform: 'scale(1.2) translateY(-5px)',
                            boxShadow: '0 15px 35px rgba(255, 107, 107, 0.9)',
                            animation: 'none'
                          },
                          '&:active': {
                            transform: 'scale(0.95)'
                          },
                          '& .MuiSvgIcon-root': {
                            animation: 'iconBounce 2.2s ease-in-out infinite',
                            transformOrigin: 'center'
                          }
                        }}
                      >
                        <SmartToyIcon />
                      </IconButton>
                    </Box>
                  </Tooltip>
                </Box>
                
                <BaseTextField
                  label="Prompt système"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  multiline
                  rows={6}
                  fullWidth
                  required
                  placeholder="Vous êtes un assistant qui résume des transcriptions de réunions..."
                  helperText="Décrivez comment l'IA doit traiter les transcriptions, ou utilisez le générateur IA pour vous aider"
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, color: '#3F51B5', fontSize: '20px', alignSelf: 'flex-start', mt: 1 }}>🤖</Box>
                    ),
                    sx: { fontFamily: 'monospace' }
                  }}
                />
              </Box>
            </Grid>

            {error && (
              <Grid item xs={12}>
                <Alert severity="error">
                  {error}
                </Alert>
              </Grid>
            )}
          </Grid>
        </form>
      )}

      {/* Dialog de génération de prompt par IA */}
      <PromptChatDialog
        open={showPromptChat}
        onClose={() => setShowPromptChat(false)}
        onPromptGenerated={handlePromptGenerated}
      />
    </BaseDialog>
  );
};

export default EditModelDialog; 